import { type FormEvent, useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { Dialog } from './Dialog.js';

const { t } = i18n;

export function PriceOverrideDialog({
  palletId,
  onClose,
  onSaved,
}: {
  palletId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [periodFrom, setPeriodFrom] = useState(today);
  const [periodTo, setPeriodTo] = useState(today);
  const [mode, setMode] = useState<'FLAT_RATE' | 'FLAT_AMOUNT'>('FLAT_RATE');
  const [valueEur, setValueEur] = useState('0.10');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const cents = Math.round(Number(valueEur.replace(',', '.')) * 100);
    if (!Number.isFinite(cents) || cents < 0) {
      setError(t('pallet.override.invalid_value'));
      return;
    }
    if (!reason.trim()) {
      setError(t('pallet.override.reason_required'));
      return;
    }
    setBusy(true);
    try {
      await api(`/pallets/${palletId}/price-overrides`, {
        method: 'POST',
        body: {
          periodFrom,
          periodTo,
          mode,
          valueCents: cents,
          reason: reason.trim(),
        },
      });
      onSaved();
    } catch {
      setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t('pallet.override.title')} onClose={onClose}>
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-2">
          <label className="block space-y-1">
            <span className="font-medium">{t('payment.col.period_from')}</span>
            <input
              type="date"
              value={periodFrom}
              onChange={(e) => setPeriodFrom(e.target.value)}
              required
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            />
          </label>
          <label className="block space-y-1">
            <span className="font-medium">{t('payment.col.period_to')}</span>
            <input
              type="date"
              value={periodTo}
              onChange={(e) => setPeriodTo(e.target.value)}
              required
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            />
          </label>
        </div>
        <label className="block space-y-1">
          <span className="font-medium">{t('pallet.override.mode')}</span>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as 'FLAT_RATE' | 'FLAT_AMOUNT')}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          >
            <option value="FLAT_RATE">{t('pallet.override.mode.FLAT_RATE')}</option>
            <option value="FLAT_AMOUNT">{t('pallet.override.mode.FLAT_AMOUNT')}</option>
          </select>
        </label>
        <label className="block space-y-1">
          <span className="font-medium">
            {mode === 'FLAT_RATE'
              ? t('pallet.override.value_per_day')
              : t('pallet.override.value_total')}{' '}
            (EUR)
          </span>
          <input
            inputMode="decimal"
            value={valueEur}
            onChange={(e) => setValueEur(e.target.value)}
            required
            className="w-full rounded border border-border bg-background px-2 py-1.5 tabular-nums"
          />
        </label>
        <label className="block space-y-1">
          <span className="font-medium">{t('pallet.override.reason')}</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            rows={2}
            placeholder={t('pallet.override.reason_hint')}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>

        {error ? <div className="text-red-600">{error}</div> : null}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded border border-border px-3 py-1.5">
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy || !reason.trim()}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
